import { Button, EButtonColor } from '@web_opxp/components';
import { AuthSelectors, LanguageSelectors, useTranslation } from '@web_opxp/features';
import * as React from 'react';
import { connect } from 'react-redux';
import { SKELETON_CLASSNAME } from '../../base/constants';
import { getUserPrivacySubpageUrl } from '../../base/historyService';
import { IState } from '../../store/state';
import { HIGHSCORES_LIST_SIZE } from '../constants';
import * as Selectors from '../selectors';
import { HighscoresActions } from '../slice';
import { HighscoreFilter } from '../types';
import './HighscoresFooter.scss';
import Pagination from './Pagination';

export interface IHighscoresFooterProps {
  filter: HighscoreFilter;
  loaded: boolean;
  page: number;
  total: number;
  privacyUrl: string;
  onChangePage: (page: number) => void;
  onFilter: (filter: HighscoreFilter) => void;
  isDataEmpty?: boolean;
}

export const HighscoresFooterView: React.FC<IHighscoresFooterProps> = ({
  filter,
  loaded,
  page,
  total,
  privacyUrl,
  onChangePage,
  onFilter,
  isDataEmpty,
}) => {
  const { t } = useTranslation();

  const skeletonClass = loaded ? '' : SKELETON_CLASSNAME;
  const renderPagination = loaded && total > 0;
  const filterCallbackBuilder = (selectedFilter: HighscoreFilter) => () => onFilter(selectedFilter);
  if (isDataEmpty) {
    return null;
  }
  return (
    <div className={`highscores-footer ${skeletonClass}`}>
      <div className="highscores-filters">
        <Button
          className="filter-button"
          color={EButtonColor.blue}
          disabled={filter === 'all'}
          label={t('Highscore.Filter.All')}
          onClick={filterCallbackBuilder('all')}
        />
        <Button
          className="filter-button"
          color={EButtonColor.white}
          disabled={filter === 'friends'}
          label={t('Highscore.Filter.Friends')}
          onClick={filterCallbackBuilder('friends')}
        />
      </div>
      <div className="highscores-settings">
        <a id="highscores-privacy-link" href={privacyUrl}>
          <small>{t('Highscore.PrivacyLink')}</small>
        </a>
      </div>
      <div className="highscores-pagination">
        {renderPagination ? (
          <Pagination
            numberOfRecords={total}
            currentPage={page}
            itemsPerPage={HIGHSCORES_LIST_SIZE}
            onChangePage={onChangePage}
          />
        ) : null}
      </div>
    </div>
  );
};

export const mapStateToProps = (state: IState) => {
  // get filter
  const filter = Selectors.getFilter(state);
  // check if data was loaded
  const loaded = !Selectors.isLoading(state);
  // current page
  const page = Selectors.getHighscoresPage(state);
  // total amount of users in rankings
  const total = Selectors.getTotalUsers(state);
  // get information needed for privacy link
  const token = AuthSelectors.getToken(state);
  const lang = LanguageSelectors.getLanguage(state);
  const privacyUrl = getUserPrivacySubpageUrl(lang, token);
  // rankings for other users
  const highscores = Selectors.getHighscores(state);
  const isDataEmpty = loaded && (!highscores || !highscores.length) && !total;
  return {
    filter,
    loaded,
    page,
    total,
    privacyUrl,
    isDataEmpty,
  };
};

export const mapDispatchToProps = {
  onChangePage: HighscoresActions.loadHighscoresPage,
  onFilter: HighscoresActions.filterHighscores,
};

const HighscoresFooter = connect(mapStateToProps, mapDispatchToProps)(HighscoresFooterView);
HighscoresFooter.displayName = 'HighscoresFooter';
export default HighscoresFooter;
